import { Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { UseMutationProps } from "@lib/types/Api";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { ProviderCustomerMatrix } from "@lib/types/Provider";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import ProviderName from "@lib/components/MiniComponents/Others/ProviderName";
import dayjs from "dayjs";
import { TableColumnsProps } from "@lib/types/General";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";

type Props<T> = TableColumnsProps<T> & {
  updateQuery: UseMutationProps<T>;
  deleteQuery: UseMutationProps<T>;
  handleSelect?: (record: T) => void;
};

const getColumns = <T extends ProviderCustomerMatrix>(
  props: Props<T>,
): ColumnsType<T> => {
  const { messages, deleteQuery, updateQuery, handleSelect } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "common.apiKey",
      dataIndex: "id",
      width: "160px",
      render: (v, r) => (
        <TableIdButton text={`#${shortenString(r.id)}`} textToBeCopied={r.id} />
      ),
      filterType: "search",
    },
    {
      title: `${messages["common.customer"] as string} ID`,
      dataIndex: "customer.id",
      width: "150px",
      render: (v, r) => (
        <TableIdButton
          text={`#${shortenString(r.customerId)}`}
          textToBeCopied={r.customerId}
        />
      ),
      filterType: "search",
    },
    {
      title: "common.customer",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "250px",
      render: (v, r) => <CustomerName customer={r?.customer} link />,
    },
    {
      title: `${messages["common.provider"] as string} ID`,
      dataIndex: "provider.id",
      width: "180px",
      render: (v, r) => (
        <TableIdButton
          text={`#${shortenString(r.providerId)}`}
          textToBeCopied={r.providerId}
        />
      ),
      filterType: "search",
    },
    {
      title: "common.provider",
      dataIndex: "provider.name",
      width: "250px",
      render: (v, r) => <ProviderName provider={r?.provider} link />,
      filterType: "search",
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "150px",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      width: "150px",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "150px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <Switch
            checked={record.enabled}
            checkedChildren={messages["common.enabled"] as string}
            unCheckedChildren={messages["common.disabled"] as string}
            onChange={(checked) => {
              updateQuery.mutate({
                ...record,
                enabled: checked,
              });
            }}
          />

          <ActionButton
            type="edit"
            onClick={() => handleSelect(record)}
            noLabel
          />

          <ActionButton
            type="delete"
            onClick={() => deleteQuery.mutate(record)}
            itemName="common.providerCustomerMatrix"
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
